import { JwtPayload } from "jwt-decode";

export interface CustomJwtPayload extends JwtPayload {
  authorities: string[];
}

export interface Link {
  href: string;
}

export interface HateoasObject {
  _links: {
    self: Link;
  };
}

export interface HateoasResponse<T> extends HateoasObject {
  _embedded: T;
}

export interface Page {
  size: number;
  totalElements: number;
  totalPages: number;
  number: number;
}

export interface PagedHateoasResponse<T> extends HateoasResponse<T> {
  page: Page;
}

export type User = HateoasObject & {
  email: string;
};

export type UsersResponse = HateoasResponse<{ users: User[] }>;

export interface KeyValueProperty {
  id: number;
  name: string;
  description?: string;
}

export type StatusesResponse = PagedHateoasResponse<{
  statuses: KeyValueProperty[];
}>;

export type FailureCodesResponse = PagedHateoasResponse<{
  failureCodes: KeyValueProperty[];
}>;

export type LifecycleEventsResponse = PagedHateoasResponse<{
  lifecycleEvents: KeyValueProperty[];
}>;

export type ProductPropertiesResponse = PagedHateoasResponse<{
  productProperties: KeyValueProperty[];
}>;

export interface DeviceApplication {
  id: number;
  device_name: string;
  application_name: string;
  description?: string;
}

export type DeviceApplicationsResponse = PagedHateoasResponse<{
  device_applications: DeviceApplication[];
}>;

export interface ApplicationVersion {
  device_uid: string;
  application: number;
  version: string;
}

export type LifecycleEventMapDataType = undefined | null | LifecycleEventData;
export interface LifecycleEventMap {
  device_uid: string;
  event: number;
  timestamp: number;
  data: LifecycleEventMapDataType;
}

export interface LifecycleEventData {
  version: number;
  data:
    | EndOfLineTestResultDataV1
    | EndOfLineTestResultDataV2
    | SoftwareUpdateRequestedDataV1
    | any;
}

export type Transport = "Bluetooth" | "Uart" | "Usb";

export interface ClientMetadata {
  client_os: string;
  client_os_version: string;
  client_app: string;
  client_app_version: string;
  transport: Transport;
  ble_rssi: undefined | null | number;
}

export interface DeviceMetadata {
  versions: { [key: string]: string };
  registered: boolean;
}

export const COMPONENT_IDS = {
  2: "Firmware",
  3: "FIDO Applet",
  4: "Bootloader",
};

export interface EndOfLineTestResultDataV1 {
  test_runner: string;
  test_runner_version: string;
  result: "PASSED" | "FAILED" | "ERROR" | "CANCELED";
  execution_time: number;
  firmware_version: string;
  bootloader_version: string;
  fido_applet_version: string;
  error_code: number | undefined | null;
  suite_results: undefined | null | EndOfLineTestSuiteResults;
  ble_rssi: undefined | null | number;
  /**
   * Hexadecimal-encoded byte array of the image. Each byte is one pixel in a
   * greyscale bitmap
   */
  fp_image: undefined | null | string;
  voltage: undefined | null | number;
}

export interface EndOfLineTestResultDataV2 {
  test_runner: string;
  client_metadata: ClientMetadata;
  device_metadata: DeviceMetadata;
  result: "PASSED" | "FAILED" | "ERROR" | "CANCELED";
  execution_time_ms: number;
  suite_results: undefined | null | EndOfLineTestSuiteResults;
  failure_codes: number[];
  error_description: undefined | null | string;
  /**
   * Hexadecimal-encoded byte array of the image. Each byte is one pixel in a
   * greyscale bitmap
   */
  fp_image: undefined | null | string;
  voltage: undefined | null | number;
}

export interface SoftwareUpdateRequestedDataV1 {
  client_metadata: ClientMetadata;
  device_metadata: DeviceMetadata;
  target_component: 2 | 3 | 4;
  authorized: boolean;
}

export interface FirstTimeSetupStartedDataV1 {
  client_metadata: ClientMetadata;
  device_metadata: DeviceMetadata;
  authorized: boolean;
}

export function endOfLineTestResultDataIsCompatible(
  version: number,
  data: any,
): data is EndOfLineTestResultDataV2 {
  return version === 2;
}

export function softwareUpdateRequestedDataIsCompatible(
  version: number,
  data: any,
): data is SoftwareUpdateRequestedDataV1 {
  return version === 1;
}

export function firstTimeSetupStartedDataIsCompatible(
  version: number,
  data: any,
): data is FirstTimeSetupStartedDataV1 {
  return version === 1;
}

export interface EndOfLineTestSuiteResults {
  BLE: undefined | null | boolean;
  BATTERY: undefined | null | boolean;
  TOUCH: undefined | null | boolean;
  FPS: undefined | null | boolean;
  NFC: undefined | null | boolean;
  LED: undefined | null | boolean;
}

export type LifecycleEventMapResponse = PagedHateoasResponse<{
  boardLifecycleEventMaps: LifecycleEventMap[];
}>;

export interface BoardProperty {
  device_uid: string;
  property: number;
  value: string;
}

export interface Board extends HateoasObject {
  device_uid: string;
  mac?: string;
  serial?: string;
  pcb_serial?: string;
  battery?: string;
  model: KeyValueProperty;
  probe: string;
  status: KeyValueProperty;
  failure_code?: KeyValueProperty;
  comments?: string;
  self_test_report: string;
  end_of_line_test_report?: string;
  last_updated: number;
  application_versions: ApplicationVersion[];
  lifecycle_events: LifecycleEventMap[];
  board_properties: BoardProperty[];
}

export interface NewBoard
  extends Partial<Omit<Board, "status" | "failure_code">> {
  status?: string;
  failure_code?: string;
}

export type BoardsResponse = PagedHateoasResponse<{
  boards: Board[];
}>;

export type ModelNumber = HateoasObject & {
  id: number;
  name: string;
  description: string;
};

export type ModelNumberResponse = HateoasResponse<{
  modelNumbers: ModelNumber[];
}>;
